/**
 * A table of shipments. Rendered when the "My shipments" page view is set to
 * "table".
 */

import React, { useState } from "react";
import PropTypes from "prop-types";

import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import IncomingShipmentsUnscheduledTableRow from "./incoming-shipments-unscheduled-table-row";
import incomingShipmentPropType from "../../prop-types/incoming-shipment";
import IncomingShipmentsUnscheduledTableColumns from "../../data/incoming-shipments-unscheduled-table-columns";
import useIncomingShipmentsViewPreferences from "../../hooks/use-incoming-shipments-view-preferences";
import { convertParentChild } from "../../components/Constants";

const IncomingShipmentsUnscheduledTable = (props) => {
  const { incomingShipments, shipmentsLoadId, warehouseId } = props;
  const [sortColumn, setSortColumn] = useState("");
  const [viewPreferences] = useIncomingShipmentsViewPreferences();
  const [sortDirection, setSortDirection] = useState("ascending");
  const expandAll = viewPreferences.get("expandAllUnscheduled");

  const displayData = expandAll ? shipmentsLoadId : incomingShipments;

  const visibleColumns = viewPreferences.get("columns");

  const onSort = ({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  if (displayData) {
    // Make a copy of incoming shipments and sort if `sortColumn` exists
    const sortedIncomingShipments = [...displayData];

    if (sortColumn) {
      if (sortColumn === "expectedAsrsUnits") {
        sortedIncomingShipments.sort((a, b) => {
          let aValue = isNaN(a.expectedAsrsUnits) ? 0 : a.expectedAsrsUnits;
          let bValue = isNaN(b.expectedAsrsUnits) ? 0 : b.expectedAsrsUnits;
          return sortDirection === "ascending"
            ? aValue - bValue
            : bValue - aValue;
        });
      } else if (sortColumn === "expectedAsrsUnitsPercent") {
        sortedIncomingShipments.sort((a, b) => {
          let aValue = isNaN(a.expectedAsrsUnitsPercent)
            ? 0
            : a.expectedAsrsUnitsPercent;
          let bValue = isNaN(b.expectedAsrsUnitsPercent)
            ? 0
            : b.expectedAsrsUnitsPercent;
          return sortDirection === "ascending"
            ? aValue - bValue
            : bValue - aValue;
        });
      } else {
        sortedIncomingShipments.sort((a, b) => {
          if (a[sortColumn] < b[sortColumn]) {
            return sortDirection === "ascending" ? -1 : 1;
          } else if (a[sortColumn] > b[sortColumn]) {
            return sortDirection === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }
    }

    const visibleIncomingShipments = sortedIncomingShipments;

    return (
      <React.Fragment>
        <Table
          headerRows={1}
          showDividers={true}
          rowComponents={[IncomingShipmentsUnscheduledTableRow, TableRow]}
          stickyHeaderRow={true}
          stickyHeaderColumn={false}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          showStripes={viewPreferences.get("stripes")}
          spacing={viewPreferences.get("spacing")}
        >
          <TableRow>
            {IncomingShipmentsUnscheduledTableColumns
              // Only include columns that have been set to "visible"
              .filter(({ id }) => visibleColumns[id])
              .map((header) => {
                // Generate dynamic labels for quantity columns based on current toggle
                let dynamicLabel = header.label;
                if (header.id === "expectedUnits") {
                  dynamicLabel = `Expected Units${convertParentChild === 1 ? ' Child' : ''}`;
                } else if (header.id === "expectedAsrsUnits") {
                  dynamicLabel = `Expected Automation Units${convertParentChild === 1 ? ' Child' : ''}`;
                }
                
                return (
                  <TableCell
                    key={header.id}
                    sortColumn={header.id}
                    width={header.width}
                    alignmentHorizontal={header.alignment}
                  >
                    {dynamicLabel}
                  </TableCell>
                );
              })}
          </TableRow>
          {visibleIncomingShipments.map((incomingShipment, index) => (
            <IncomingShipmentsUnscheduledTableRow
              key={incomingShipment.id}
              incomingShipment={incomingShipment}
              warehouseId={warehouseId}
            />
          ))}
        </Table>
      </React.Fragment>
    );
  }
};

IncomingShipmentsUnscheduledTable.propTypes = {
  visibleIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
  incomingShipments: PropTypes.arrayOf(incomingShipmentPropType),
  warehouseId: PropTypes.string.isRequired,
};

IncomingShipmentsUnscheduledTable.defaultProps = {
  areAllSelected: false,
};

export default IncomingShipmentsUnscheduledTable;
